<template>
  <div
    :class="{
      'vue-treeselect-container v-input roundish v-text-field--enclosed v-text-field--outlined v-text-field v-select': true,
      'v-input--is-focused primary--text': hasFocus,
      'dense v-input--dense': dense,
      borderless: borderless,
      'multiple-mode': multiple,
      'error--text v-input--has-state': error && !addLeafNode.show,
    }"
  >
    <div class="v-input__control" v-if="!addLeafNode.show">
      <div
        role="button"
        aria-haspopup="listbox"
        class="v-input__slot"
        :aria-owns="id"
      >
        <fieldset aria-hidden="true">
          <legend :style="`width: ${labelWidth};`">
            <span class="notranslate">&ZeroWidthSpace;</span>
          </legend>
        </fieldset>
      </div>

      <div class="v-select__slot vue-treeselect-slot">
        <label
          :for="id"
          :class="[
            'v-label vue-treeselect-label',
            hasFocus || pickerValue ? 'has-focus' : '',
          ]"
          >{{ label }}</label
        >
        <div class="v-select__selections">
          <treeselect
            :value="pickerValue"
            :clearable="clearable"
            :disabled="readonly"
            :show-count="false"
            :disableBranchNodes="!fulldetail"
            :options="localOptions"
            :placeholder="placeholder"
            :name="id"
            :searchNested="true"
            :startSearchLength="5"
            :waitSearchFinishTime="500"
            :disableFuzzyMatching="true"
            :multiple="multiple"
            :limit="limit"
            :limitText="limitText"
            :class="{
              dense: dense,
              'has-focus': hasFocus,
              'has-value': !!pickerValue,
              'validation-error': error,
              borderless: borderless,
            }"
            @input="updateValue($event)"
            @open="gainedFocus"
            :autoFocus="autofocus"
            :openOnFocus="autofocus"
            appendToBody
            :flat="returnFullDetail"
            ref="treeselect"
            :instanceId="inputElementID"
          >
            <!-- @close="lostFocus" -->
            <label
              slot="option-label"
              slot-scope="{
                node,
                shouldShowCount,
                count,
                labelClassName,
                countClassName,
              }"
              :class="labelClassName"
              :title="node.label"
              v-testid="testid + '_hn_' + node.raw.node_id"
            >
              <v-icon v-if="!node.isBranch && node.raw.isAddNode"
                >mdi-plus-circle</v-icon
              >
              {{ node.raw.isAddNode ? "Add new title" : node.label }}
              <span v-if="shouldShowCount" :class="countClassName"
                >({{ count }})</span
              >
            </label>
            <span
              slot="value-label"
              slot-scope="{ node }"
              :title="node.label"
              >{{ node.label }}</span
            >
          </treeselect>
        </div>
      </div>
    </div>
    <div
      v-show="error && hint && !addLeafNode.show"
      class="vue-treeselect-hint"
    >
      {{ hint }}
    </div>
    <AdminHierarchyNodeDetail
      :node="addLeafNode.parent"
      mode="add"
      :show="addLeafNode.show"
      @close="cancelAddLeafNode"
      @saved="nodeAdded"
    >
    </AdminHierarchyNodeDetail>
    <!-- Kept last and moved to document.body on mount (see mounted) so it can
         sit above the treeselect dropdown, which is also appended to body. -->
    <div
      ref="hiddenListPopover"
      v-show="hiddenList.show && hiddenValues.length"
      class="vue-treeselect-hidden-values"
      :style="{ top: hiddenList.top + 'px', left: hiddenList.left + 'px' }"
      @mouseenter="cancelHideHiddenList"
      @mouseleave="scheduleHideHiddenList"
    >
      <div
        v-for="item in hiddenValues"
        :key="item.id"
        class="vue-treeselect-hidden-values__item"
      >
        <span class="vue-treeselect-hidden-values__label" :title="item.label">{{
          item.label
        }}</span>
        <v-icon
          small
          class="vue-treeselect-hidden-values__remove"
          @click="removeHiddenValue(item.id)"
          >mdi-close</v-icon
        >
      </div>
    </div>
  </div>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import utils from "../common/utils";
import AdminHierarchyNodeDetail from "@/components/hierarchy/cAdminHierarchyNodeDetail";

export default {
  name: "HierarchyTreePicker",
  components: {
    Treeselect,
    AdminHierarchyNodeDetail,
  },
  props: {
    value: {},
    placeholder: { type: String },
    label: { type: String },
    hint: { type: String },
    dense: { type: Boolean },
    error: { type: Boolean },
    options: [],
    fulldetail: { type: Boolean },
    textValue: { type: String },
    readonly: { type: Boolean },
    clearable: { type: Boolean },
    hierarchyType: { type: Object },
    init: { type: Boolean },
    multiple: { type: Boolean },
    limit: { type: Number },
    autofocus: { type: Boolean },
    borderless: { type: Boolean },
    returnFullDetail: { type: Boolean },
    suffixFilter: { type: String, default: null },
    elementID: { type: String, default: utils.makeid(8) },
    testid: { type: String },
  },
  data: function () {
    return {
      pickerValue: this.value,
      originalValue: this.value,
      hasFocus: false,
      localOptions: [],
      initialised: false,
      addLeafNode: {
        show: false,
        label: null,
        parent: null,
        value: null,
      },
      id: "treeselect_" + utils.makeid(8),
      inputElement: null,
      inputElementID: "tvID_" + utils.makeid(8),
      hiddenList: { show: false, top: 0, left: 0 },
      hideTimer: null,
    };
  },
  created() {
    this.setLocalOptions();
    this.initialiseAddNode();
    this.expandSelected();
    this.$nextTick(() => {
      const input = this.$refs.treeselect?.$el.querySelector(
        "input.vue-treeselect__input"
      );
      if (input) {
        input.id = this.id;
        this.inputElement = input;
        input.addEventListener("focus", this.gainedFocus);
        input.addEventListener("blur", this.lostFocus);
      }
      this.bindLimitTipHover();
    });
  },
  mounted() {
    // Move the popover to body so it escapes any ancestor stacking context and
    // can render above the treeselect dropdown (also appended to body). Vue's
    // teardown removes nodes via their live parentNode, so this is safe.
    if (this.$refs.hiddenListPopover) {
      document.body.appendChild(this.$refs.hiddenListPopover);
    }
  },
  updated() {
    if (!this.hierarchyType || !this.hierarchyType.allowAddLeafNode) {
      this.addLeafNode.show = false;
      this.addLeafNode.value = null;
    }
    this.bindLimitTipHover();
    if (this.hiddenList.show && !this.hiddenValues.length)
      this.hiddenList.show = false;
  },
  computed: {
    labelWidth() {
      if (this.label && (this.hasFocus || this.pickerValue))
        return `${this.label.length * 7}px`;
      else return "0";
    },
    // The values collapsed behind the "+ N" pill (everything past the display
    // limit), resolved to { id, label } — shown in the hover popover.
    hiddenValues() {
      if (!this.multiple || !this.limit || !Array.isArray(this.pickerValue))
        return [];
      return this.pickerValue.slice(this.limit).map((id) => {
        const node = this.getTreeNode("id", id);
        return { id, label: node ? node.label || node.name : id };
      });
    },
  },
  beforeDestroy() {
    this.cancelHideHiddenList();
    // Remove the body-mounted popover so it doesn't outlive the component.
    if (this.$refs.hiddenListPopover) {
      this.$refs.hiddenListPopover.remove();
    }
    if (this.inputElement) {
      this.inputElement.removeEventListener("focus", this.gainedFocus);
      this.inputElement.removeEventListener("blur", this.lostFocus);
      this.inputElement = null;
    }

    // Clean up DOM menu containers that were appended to body
    // ONLY remove menu containers associated with THIS instance
    if (this.$refs.treeselect) {
      try {
        // Get the menu container for THIS specific instance only
        const treeselectEl = this.$refs.treeselect.$el;
        if (treeselectEl) {
          const instanceId = treeselectEl.getAttribute('data-instance-id');
          if (instanceId) {
            // Only remove menu containers matching this instance
            const menuContainers = document.querySelectorAll(`.vue-treeselect__menu-container[data-instance-id="${instanceId}"]`);
            menuContainers.forEach(container => {
              if (container && container.parentNode) {
                try {
                  container.parentNode.removeChild(container);
                } catch (e) {
                  // Ignore if already removed
                }
              }
            });
          }
        }
      } catch (e) {
        // Ignore errors during cleanup
      }

      // Clear treeselect ref
      this.$refs.treeselect = null;
    }

    // Deep clean localOptions to break circular references
    const deepClean = (arr) => {
      if (!arr) return;
      arr.forEach(item => {
        if (item) {
          if (item.children) {
            deepClean(item.children);
            item.children = null;
          }
          item.parents = null;
          item.isDefaultExpanded = null;
        }
      });
    };
    deepClean(this.localOptions);

    // Clear all data
    this.localOptions = [];
    this.pickerValue = null;
    this.originalValue = null;
    this.addLeafNode = {
      show: false,
      label: null,
      parent: null,
      value: null,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (this.initialised) {
          this.pickerValue = val;
        }
      },
    },
    options() {
      this.setLocalOptions();
    },
    suffixFilter() {
      this.setLocalOptions();
    },
    "hierarchyType.allowAddLeafNode"() {
      this.initialiseAddNode();
    },
    init(val) {
      if (val) {
        this.setLocalOptions();
        this.initialiseAddNode();
      }
    },
    pickerValue() {
      this.expandSelected();
    },
  },
  methods: {
    limitText(count) {
      return `+ ${count}`;
    },
    // vue-treeselect exposes no slot for the limit tip, so attach the hover
    // handlers that drive our custom popover directly to its element. Guarded
    // by a dataset flag so re-renders don't stack duplicate listeners.
    bindLimitTipHover() {
      this.$nextTick(() => {
        const el = this.$refs.treeselect?.$el?.querySelector(
          ".vue-treeselect__limit-tip"
        );
        if (el && !el.dataset.hiddenListBound) {
          el.dataset.hiddenListBound = "1";
          el.addEventListener("mouseenter", this.showHiddenList);
          el.addEventListener("mouseleave", this.scheduleHideHiddenList);
        }
      });
    },
    showHiddenList(event) {
      if (!this.hiddenValues.length) return;
      this.cancelHideHiddenList();
      const rect = event.currentTarget.getBoundingClientRect();
      this.hiddenList.top = rect.bottom + 4;
      this.hiddenList.left = rect.left;
      this.hiddenList.show = true;
      // Popover lives at body level (outside .v-application), so drive the dark
      // theme via a class toggled here rather than an ancestor selector.
      const popover = this.$refs.hiddenListPopover;
      if (popover) {
        popover.classList.toggle("theme--dark", !!this.$vuetify.theme.dark);
      }
    },
    scheduleHideHiddenList() {
      this.cancelHideHiddenList();
      // Small delay so moving from the pill onto the popover doesn't close it.
      this.hideTimer = setTimeout(() => {
        this.hiddenList.show = false;
      }, 200);
    },
    cancelHideHiddenList() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    removeHiddenValue(id) {
      const current = Array.isArray(this.pickerValue)
        ? this.pickerValue.slice()
        : [];
      const newValue = current.filter((v) => v !== id);
      this.pickerValue = newValue;
      this.updateValue(newValue);
      this.$nextTick(() => {
        if (!this.hiddenValues.length) this.hiddenList.show = false;
      });
    },
    filterOptions() {
      let filter = (options, suffix) => {
        return options.reduce((acc, o) => {
          if (o.children) {
            const filteredChildren = filter(o.children, suffix);
            if (filteredChildren.length > 0) {
              acc.push({ ...o, children: filteredChildren });
            }
          } else if (!suffix || o.label.endsWith(suffix)) {
            acc.push({ ...o });
          }
          return acc;
        }, []);
      }

      return filter(this.options, this.suffixFilter);
    },
    setLocalOptions() {
      // Deep copy options to avoid mutating the parent's data
      // this.localOptions = JSON.parse(JSON.stringify(this.filterOptions()));
      this.localOptions = this.filterOptions();
    },
    updateValue(value) {
      let newValue = value;
      if (this.hierarchyType?.allowAddLeafNode) {
        this.addLeafNode.show = false;
        if (value < 0 && value > -999) {
          this.addLeafNode.show = true;
          let selNode = this.getNode("id", value);
          let parentNode = this.getTreeNode("node_id", selNode.node_id_parent);
          this.addLeafNode.label = `New ${
            this.hierarchyType["level" + this.hierarchyType.linklevel + "_name"]
          }`;
          newValue = this.originalValue;
          this.addLeafNode.parent = {
            parentOption: parentNode,
            hierarchy_node_id: selNode.node_id_parent,
            ht_id: this.hierarchyType.ht_id,
            level: this.hierarchyType.linklevel - 1,
            linklevel: this.hierarchyType.linklevel,
            nextLevel:
              this.hierarchyType[`level${this.hierarchyType.linklevel}_name`],
            level_name:
              this.hierarchyType[
                `level${this.hierarchyType.linklevel - 1}_name`
              ],
          };
          return;
        }
      }
      if (this.fulldetail) {
        const selNodeIds = [];
        (this.multiple ? newValue : [newValue]).forEach((v) => {
          const selNode = this.getTreeNode("id", v);
          if (selNode && selNode.node_id) this.returnFullDetail ? selNodeIds.push(selNode) : selNodeIds.push(selNode.node_id);
        });
        this.$emit("changeNodeIds", selNodeIds);
      }

      this.$emit("input", newValue);
      this.$emit("change", newValue);
    },
    checkForUnclassified(val) {
      let checkExists = function (options, id) {
        let exists = false;
        options.forEach((x) => {
          if (!exists) {
            if (x.children) {
              exists = checkExists(x.children, id);
            } else {
              exists = x.id === id;
            }
          }
        });
        return exists;
      };
      let that = this;
      let insertUnclassified = function (options, v) {
        if (v && !checkExists(options, v)) {
          options.push({
            id: `Unclassified${v}`,
            label: "Unclassified",
            children: [
              { id: v, label: `${that.textValue || v} [Unclassified]` },
            ],
          });
        }
      };
      if (Array.isArray(val)) {
        val.forEach((v) => insertUnclassified(this.localOptions, v));
      } else {
        insertUnclassified(this.localOptions, val);
      }
    },
    initialiseAddNode(checkPrevious) {
      if (checkPrevious && this.initialised) return;
      this.addLeafNode.show = false;
      this.addLeafNode.value = null;
      let hasAdd = !!this.getNode("isAddNode", true);
      if (hasAdd) {
        let remAdd = (nodes) => {
          let index = nodes.findIndex((x) => x.isAddNode);
          if (index >= 0) {
            nodes.splice(index, 1);
          }
          nodes.forEach((n) => {
            if (n.children) {
              remAdd(n.children);
            }
          });
        };
        remAdd(this.localOptions);
      }

      if (this.hierarchyType?.allowAddLeafNode) {
        let count = 0;
        let addNode = (nodes, parent) => {
          let isEnd = false;
          nodes.forEach((n) => {
            if (n.children) {
              addNode(n.children, n);
            } else if (!n.isActionTrigger) {
              isEnd = true;
            }
          });
          if (isEnd) {
            count--;
            nodes.unshift({
              id: count,
              label: " " + parent?.label,
              isAddNode: true,
              node_id_parent: parent?.node_id,
            });
          }
        };
        addNode(this.localOptions);
      }
      if (!this.multiple) this.checkForUnclassified(this.pickerValue);
      this.initialised = true;
    },
    reset() {
      const resetExpanded = (nodes) => {
        nodes.filter(n => n.isDefaultExpanded).forEach(n => {
          n.isDefaultExpanded = false;
          if (n.children) resetExpanded(n.children);
        });
      }
      resetExpanded(this.localOptions);
    },
    expandSelected() {
      const node = this.getTreeNode("id", this.pickerValue);
      this.reset();
      if (node && node.parents && node.parents.length) {
        let opts = this.localOptions;
        node.parents.forEach((p) => {
          const parent = opts.find((o) => o.name === p);
          if (parent) {
            parent.isDefaultExpanded = true;
            opts = parent.children;
          }
        });
      }
    },
    nodeAdded(details) {
      if (!details.newNode) return;
      details.newNode.ht_name = this.hierarchyType.label.replace("Select ", "");
      let newOption = {
        id: details.newNode.hierarchy_node.hr_id,
        label: details.newNode.hierarchy_node.name,
        name: details.newNode.hierarchy_node.name,
        node_id: details.newNode.hierarchy_node.hierarchy_node_id,
      };
      if (this.addLeafNode.parent.parentOption)
        this.addLeafNode.parent.parentOption.children.unshift(newOption);
      else this.localOptions.unshift(newOption);
      this.pickerValue = newOption.id;
      this.addLeafNode.show = false;
      this.$emit("nodeAdded", details.newNode);
    },
    getNode(property, value) {
      let _getNode = function (options, property, value) {
        let exists = null;
        options.forEach((x) => {
          if (!exists) {
            if (x.children) {
              exists = _getNode(x.children, property, value);
              if (exists) exists.parents.unshift(x.name);
            } else {
              exists = x[property] === value ? x : null;
              if (exists) exists.parents = [];
            }
          } else {
            return;
          }
        });
        return exists;
      };
      return value ? _getNode(this.localOptions, property, value) : null;
    },
    getTreeNode(property, value) {
      let _getNode = function (options, property, value) {
        let exists = null;
        options.forEach((x) => {
          if (!exists) {
            exists = x[property] === value ? x : null;
            if (exists) exists.parents = [];
            else if (x.children) {
              exists = _getNode(x.children, property, value);
              if (exists) exists.parents.unshift(x.name);
            }
          } else {
            return;
          }
        });
        return exists;
      };
      return value ? _getNode(this.localOptions, property, value) : null;
    },
    cancelAddLeafNode() {
      this.addLeafNode.value = null;
      this.addLeafNode.show = false;
      this.lostFocus();
    },
    gainedFocus() {
      // Use nextTick to ensure menu is rendered, then query once
      this.$nextTick(() => {
        // const menuContainer = this.$refs.treeselect?.$el?.querySelector('.vue-treeselect__menu-container');
        const menuContainer = this.$refs[this.inputElementID]?.$el?.querySelector('.vue-treeselect__menu-container');
        if (menuContainer) {
          if (this.$vuetify.theme.dark) {
            menuContainer.classList.add('theme--dark');
          } else {
            menuContainer.classList.remove('theme--dark');
          }
        }
      });

      this.hasFocus = true;
      this.$emit("focus");
    },
    lostFocus() {
      if (this.addLeafNode.show) return;
      this.hasFocus = false;
      this.$emit("lostFocus");
    },
  },
};
</script>
<style lang="scss">
@import "@/assets/styles/vars";

.vue-treeselect-container.borderless {
  .v-input__slot,
  .vue-treeselect-label {
    display: none;
  }
}

.vue-treeselect-label {
  position: absolute;
  right: auto;
  left: 18px;
  top: 30px !important;

  &.has-focus {
    left: 12px;
    top: 2px !important;
    font-size: 12px !important;
  }
}

.vue-treeselect-slot {
  left: 2px;
  right: auto;
  top: -10px;
  position: absolute !important;
}

.vue-treeselect-hint {
  position: absolute;
  left: 5px;
  top: 50px;
  font-size: 12px;
  margin: 5px 12px;
}

// Hover popover listing the values collapsed behind the "+ N" pill, each with
// a remove button. Fixed-positioned so it escapes the control's overflow.
.vue-treeselect-hidden-values {
  position: fixed;
  // Above the treeselect dropdown menu (appended to body, z-index 999).
  z-index: 1000;
  max-width: 280px;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  font-size: 12px;
  font-family: "DM Sans", sans-serif;

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 10px;

    &:hover {
      background: #f5f5f5;
    }
  }

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__remove {
    cursor: pointer;
    font-size: 16px !important;
    color: #039be5 !important;

    &:hover {
      color: #e53935 !important;
    }
  }
}

.vue-treeselect-hidden-values.theme--dark {
  background: $primary-background-dark;
  border-color: $secondary-background-dark;
  color: white;

  .vue-treeselect-hidden-values__item:hover {
    background: $secondary-background-dark;
  }
}

.dense {
  .vue-treeselect-label {
    left: 13px;
    top: 21px !important;
    &.has-focus {
      left: 12px;
      top: 2px !important;
    }
  }
  .vue-treeselect-hint {
    top: 32px;
  }
}

.vue-treeselect-hint-addLeafNode {
  position: relative;
  left: 5px;
  top: 0px;
  font-size: 12px;
  margin: 5px 12px;
}

.vue-treeselect {
  font-family: "Martel Sans", sans-serif;
  color: $input-text-color;
  margin-top: 3px;
  .vue-treeselect__menu-container {
    margin-top: 1px;
  }

  .vue-treeselect__control {
    border-radius: 14px;
    margin-right: 10px;
    border: 0;
    height: 56px;
    font-size: 1rem;
  }

  .vue-treeselect__single-value,
  .vue-treeselect__value-container,
  .vue-treeselect__placeholder {
    padding: 12px 0 10px 12px;
    height: 44px;
  }

  .vue-treeselect__input-container {
    padding: 0 0 10px 0;
    height: 34px;
  }

  .vue-treeselect__control-arrow-container {
    padding: 0 28px 0 0;
  }

  &:not(.has-focus) .vue-treeselect__placeholder,
  &.has-value .vue-treeselect__placeholder {
    display: none;
  }

  &.dense {
    margin-top: 0;
    .vue-treeselect__control {
      height: 35px !important;
    }
    .vue-treeselect__single-value,
    .vue-treeselect__value-container,
    .vue-treeselect__placeholder {
      padding: 8px 0 2px 8px;
      height: 34px;
    }

    .vue-treeselect__input-container {
      padding: 0 0 14px 0;
      height: 34px;
    }

    .vue-treeselect__x-container {
      padding-top: 6px;
    }

    .vue-treeselect__control-arrow-container {
      padding: 6px 28px 0 0;
    }
  }

  &.borderless {
    .vue-treeselect__control {
      border-width: 0 0 1px 0;
      border-radius: 0;
      height: 26px;
    }
    &.has-focus .vue-treeselect__control {
      border-width: 0 0 2px 0 !important;
    }
    .vue-treeselect__single-value,
    .vue-treeselect__value-container,
    .vue-treeselect__placeholder {
      padding: 0;
      font-size: 14px;
    }
  }

  &.validation-error {
    .vue-treeselect__control {
      border-color: #ff5252;
      caret-color: #ff5252;
      border-width: 2px;
    }
    .vue-treeselect__single-value,
    .vue-treeselect__value-container,
    .vue-treeselect__placeholder,
    .vue-treeselect__control-arrow {
      color: #ff5252;
    }
  }

  // In multiple mode the selected values render as inline-block tags that wrap
  // on their own. The fixed heights above pin the control/value-container to a
  // single row, so the wrapped rows spill past the border. Releasing the fixed
  // height (min-height keeps the single-row size) lets it grow instead.
  // Scope to --multi so single-mode usages are untouched.
  &.vue-treeselect--multi {

    // Render the "+ N" overflow indicator as a pill matching the value tags
    // instead of the default plain grey "and N more" text.
    .vue-treeselect__limit-tip-text {
      margin: 0;
      padding: 2px 8px;
      background: #e3f2fd;
      color: #039be5;
      border-radius: 2px;
      font-size: 12px;
      font-weight: 600;
    }

    // Keep the whole value display on a single line: lay the chips, the "+ N"
    // pill and the search input out in a flex row that never wraps. The chip(s)
    // shrink and ellipsis-truncate to make room; the "+ N" pill never shrinks so
    // it always stays visible on the same line.
    .vue-treeselect__multi-value {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      width: 100%;
      min-width: 0;
    }
    .vue-treeselect__multi-value-item-container {
      flex: 0 1 auto; // shrink when space is tight, don't grow past content
      min-width: 0;
      padding-top: 0;
      overflow: hidden;
    }
    .vue-treeselect__multi-value-item {
      display: flex;
      align-items: center;
      max-width: 100%;
      min-width: 0;
    }
    .vue-treeselect__multi-value-label {
      display: block;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .vue-treeselect__value-remove {
      flex: 0 0 auto; // the "x" never shrinks
    }
    .vue-treeselect__limit-tip {
      flex: 0 0 auto; // the "+ N" pill is always fully visible
      padding-top: 0;
    }
    .vue-treeselect__input-container {
      // Always keep room to type: grow to fill leftover space but never shrink
      // below a usable width, forcing the chip(s) to truncate earlier instead.
      flex: 1 0 auto;
      min-width: 40px;
    }

    .vue-treeselect__single-value,
    .vue-treeselect__value-container {
      padding: 4px 0 0 8px;
    }

    .vue-treeselect__input-container {
      padding: 0 0 14px 0;
      height: 39px;
      .vue-treeselect__input {
        margin-top: 6px;
      }
    }

    .vue-treeselect__x-container {
      padding-top: 0;
    }

    .vue-treeselect__control-arrow-container {
      padding: 0 28px 0 0;
      height: 50px;
    }

    &.borderless .vue-treeselect__control {
      height: auto;
      min-height: 26px;
    }
  }
}
.hierarchypicker-hierarchy-level {
  border: solid 1px #004d40;
  border-radius: 5px;
  display: block;
  height: 24px;
  font-size: 12px;
  padding-left: 6px;
  padding-right: 6px;
  margin-bottom: 5px;
  //   font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.hierarchypicker-container {
  border: rgb(221, 221, 221) solid 1px;
  border-radius: 14px;
  padding: 10px;
  width: 100%;
}
.hierarchypicker-container-heading {
  margin-top: -20px;
  font-size: 12px;
  margin-bottom: 7px;
  background-color: white;
  display: table;
}

.vue-treeselect__menu-container.theme--dark {
  .vue-treeselect__menu {
    background-color: $primary-background-dark;
    border-color: $secondary-background-dark;

    .vue-treeselect__option--highlight {
      background-color: $secondary-background-dark;
      color: white;
      .vue-treeselect__option-arrow {
        color: white;
      }
    }
  }
}

</style>